// `/building` IS THE APP'S GENERATION OVERLAY (WEB-PARITY-5: BL-1, BL-4, BL-5; LetterGenerationOverlay.swift, built by
// GenerateView.swift:1542-1601). The app draws it as a full-screen cover over the Letter step while the letter is
// written; so does this page, over `/you` — `/you` and `/building` are one screen (`./You.tsx`), with this cover closed
// or open. A route of its own still, so a reload or a shared link finds the letter being written.
//
// THE HEADER STAYS (WALK-FIX-4, R243). Tyler, 2026-10-06: "while its building.. there is no properties
// tab so Id have to click back or something". The cover starts under the website's header (logo, Properties, Account),
// which stays visible and pressable; leaving is safe, because coming back resumes the letter (WEB-RESUME).
//
// WHAT IS THE SERVER'S AND WHAT IS NOT. Whether the letter is queued, being written, shipped or failed — and how long
// to wait before asking again (`pollAfterMs`) — is `v2GenerateLetter` `fetch`'s answer, read and never guessed. The
// checklist's ticking is the app's own animation (`LetterGenerationModel.run`): a UI clock over the rows, which reads
// no fact but whether the job is done (`./generateRules.ts` `overlayState`). Its rows are built from the appeal's own
// served counts, as the app builds them from the appeal.
//
// ON SUCCESS: "Your letter is ready", then Review opens by itself (GenerateView.swift:1356-1361). ON FAILURE: the cover
// closes and the Letter step shows the app's error card (`./You.tsx`), never a page of its own (BL-4). A JOB THE SERVER
// NO LONGER COUNTS AS BEING WRITTEN (`inFlight: false`, WEB-RESUME): the cover stops asking and closes onto the Letter
// step, which reads the job back and says the letter did not finish — never a checklist that spins for a job nothing
// will finish.

import { useEffect, useId, useRef, useState } from "react";
import type { LetterJobView } from "@contract";
import { useAppealId, useSession } from "../../app/session";
import { useCall } from "../../app/useCall";
import { HeldLine } from "../../components/copy";
import { heldText } from "../../components/heldCopy";
import { Refusal } from "../../components/Refusal";
import { Icon, type IconName } from "./parity4Parts";
import {
  OVERLAY_TICK_MS, overlayRows, overlayState, type OverlayFacts, type RowIcon, type RowState,
} from "./generateRules";
import { sayG, type GenerateWordingId } from "./generateWording";

type Failed = Extract<LetterJobView, { status: "failed" }>;

const ROW_ICON: Readonly<Record<RowIcon, IconName>> = {
  house: "house", bars: "bars", scale: "scale", list: "list", wrench: "wrench", photo: "photo", pencil: "pencil", doc: "doc",
};

/** The app waits this long on "Your letter is ready" before Review opens (GenerateView.swift:1359). */
export const READY_HOLD_MS = 700;

function Said({ id, as: Tag = "span", className }: { id: GenerateWordingId; as?: "h1" | "p" | "span" | "small"; className?: string }) {
  return <Tag className={className} data-held-copy={id}>{sayG(id)}</Tag>;
}

export function BuildingOverlay({
  generationAt,
  facts,
  fresh,
  onShipped,
  onFailed,
  onStalled,
}: {
  generationAt: number;
  /** This tab has just asked for the letter (the Generate button), so the checklist always runs to its end; a reload or
   *  a shared link of a letter already written goes straight to Review. */
  fresh: boolean;
  /** The checklist's subjects, once the Letter step has them; null shows the header alone. */
  facts: OverlayFacts | null;
  onShipped: () => void;
  onFailed: (failed: Failed) => void;
  /** The server says the job is no longer being written (`inFlight: false`). */
  onStalled: () => void;
}) {
  const session = useSession();
  const appealId = useAppealId();
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(startedAt);
  const [poll, setPoll] = useState(0);
  const [doneAt, setDoneAt] = useState<number | null>(null);
  const state = useCall(
    () => session.transport.v2GenerateLetter.fetch({ appealId, generationAt }),
    [session.transport, appealId, generationAt, poll],
  );
  const job = state.status === "ok" ? state.response.body.job : null;
  // Asked again only while the SERVER counts the job as being written (`inFlight`, WEB-RESUME).
  const waiting = job !== null && (job.status === "queued" || job.status === "generating") && job.inFlight;
  const firstAnswer = useRef(true);

  // The cover takes the focus, as a cover does, and gives it back when it closes. It is NOT a modal (WALK-FIX-4): the
  // header above it stays on screen and reachable — Shift+Tab from here goes to Account and Properties, and a screen
  // reader can still find them — while the rail and the step under the cover are `inert`.
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => before?.focus?.();
  }, []);

  // The UI clock: the app's 120 ms hold tick. Nothing here asks the server.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), OVERLAY_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // The SERVER says how long to wait. `pollAfterMs` is a contract literal, not a number chosen here.
  useEffect(() => {
    if (!waiting || job === null) return;
    const timer = setTimeout(() => setPoll((p) => p + 1), job.pollAfterMs);
    return () => clearTimeout(timer);
  }, [waiting, job, poll]);

  // Shipped or failed: a letter already finished when this cover opened (a reload, not this tab's own request) goes
  // straight on; one this tab asked for, or one that finishes while the cover is open, completes the checklist first, as
  // the app's reveal does.
  useEffect(() => {
    if (job === null) return;
    const first = firstAnswer.current;
    firstAnswer.current = false;
    if (job.status === "failed") {
      onFailed(job);
      return;
    }
    if ((job.status === "queued" || job.status === "generating") && !job.inFlight) {
      onStalled();
      return;
    }
    if (job.status === "shipped") {
      if (first && !fresh) onShipped();
      else setDoneAt((d) => d ?? Date.now());
    }
  }, [job, fresh, onFailed, onShipped, onStalled]);

  const rows = facts === null ? [] : overlayRows(facts);
  const elapsed = now - startedAt;
  const view = overlayState(rows.length, elapsed, doneAt !== null, job?.status === "queued");

  useEffect(() => {
    if (!view.allDone) return;
    const timer = setTimeout(onShipped, READY_HOLD_MS);
    return () => clearTimeout(timer);
  }, [view.allDone, onShipped]);

  const activeIndex = view.rows.findIndex((r) => r === "active");
  const labelOf = (i: number): string =>
    i === rows.length - 1 && view.lastLabel !== null ? sayG(view.lastLabel) : rows[i].text;

  return (
    <div
      className="gen-overlay"
      data-overlay
      data-job-status={job?.status ?? "pending"}
      data-poll-after-ms={job !== null && (job.status === "queued" || job.status === "generating") ? job.pollAfterMs : undefined}
    >
      <div
        ref={dialog}
        tabIndex={-1}
        className="gen-overlay-inner"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
      >
        <header className="gen-overlay-head">
          <span className={view.allDone ? "gen-pulse done" : "gen-pulse"} aria-hidden="true">
            <Icon name={view.allDone ? "seal" : "sparkles"} size={34} />
          </span>
          <h1 id={titleId} data-held-copy={view.allDone ? "build.ready" : "build.title"}>
            {sayG(view.allDone ? "build.ready" : "build.title")}
          </h1>
          {view.allDone ? (
            <Said id="build.readySub" as="p" className="gen-overlay-sub" />
          ) : (
            <HeldLine id="building.subtitle" className="gen-overlay-sub" />
          )}
        </header>

        {/* A fault on our side: the app's heading for a letter that did not finish (WALK-FIX-1, ../../components/Refusal.tsx). */}
        {state.status === "failed" && (
          <Refusal
            failure={state.failure}
            context="Your letter"
            faultHeading={<h1 data-held-copy="gen.error.title">{sayG("gen.error.title")}</h1>}
          />
        )}

        {rows.length > 0 && (
          <ol className="gen-checklist" data-rows={rows.length}>
            {rows.map((row, i) => {
              const s: RowState = view.rows[i];
              return (
                <li key={row.id} className={`gen-row ${s}`} data-row={row.id} data-state={s}>
                  <span className="gen-row-disc" aria-hidden="true">
                    {s === "done" ? <Icon name="tick" size={16} /> : s === "active" ? <span className="gen-spin" /> : <Icon name={ROW_ICON[row.icon]} size={16} />}
                  </span>
                  <span className="gen-row-text" data-held-copy={i === rows.length - 1 && view.lastLabel !== null ? view.lastLabel : row.id}>
                    {labelOf(i)}
                  </span>
                  {s === "done" && <Icon name="checkCircle" size={16} className="gen-row-tick" />}
                </li>
              );
            })}
          </ol>
        )}
        {/* What a screen reader hears: the row being worked on, as it changes. */}
        <p className="visually-hidden" role="status" aria-live="polite">
          {activeIndex >= 0 && rows.length > 0 ? labelOf(activeIndex) : ""}
        </p>

        <div className="gen-overlay-notes">
          {view.longWait ? <Said id="build.longWait" as="p" /> : <Said id="build.note" as="p" />}
        </div>

        <div className="gen-overlay-progress">
          <span
            className="gen-progress-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(view.progress * 100)}
            aria-label={heldText("loading.appeal")}
          >
            <span className="gen-progress-fill" style={{ width: `${Math.max(3, view.progress * 100)}%` }} />
          </span>
          <Said id={view.allDone ? "build.complete" : "build.progress"} as="small" />
        </div>
      </div>
    </div>
  );
}
